import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatError, MatFormField } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderDurationInputComponent,
  ElderFormFieldDenseDirective,
  ElderMinValidator,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderValidationErrorDirective,
  Filter,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Duration } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { OptimisationDay } from '../optimisation-day'
import { OptimisationDayFilter, OptimisationDayService } from '../optimisation-day.service'

@Component({
  selector: 'app-optimisation-day-browser',
  templateUrl: './optimisation-day-browser.component.html',
  styleUrls: ['./optimisation-day-browser.component.scss'],
  imports: [
    ElderTableComponent,
    ElderTableActivationDirective,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatInput,
    FormsModule,
    ElderMinValidator,
    MatError,
    ElderValidationErrorDirective,
    ElderDurationInputComponent,
    ElderTranslatePipe,
  ],
})
export class OptimisationDayBrowserComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly facilityId$ = new BehaviorSubject<string>(null)
  public readonly data: IDataContext<OptimisationDay>
  private optimisationDayService = inject(OptimisationDayService)
  private toastService = inject(ElderToastService)

  @Input()
  public requestSave: Observable<Event>

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<OptimisationDay>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .build(this.optimisationDayService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.requestSave.subscribe((_) => {
      const days = this.data?.snapshot.data
      if (days && days.length > 0) {
        this.save(days)
      }
    })
  }

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facilityId(facilityId: string) {
    this.facilityId$.next(facilityId)

    if (facilityId) {
      this.filters = [OptimisationDayFilter.facilityId(facilityId)]
      this.data.start([new Sort('dayOffset', 'asc')])
    }
  }

  public get facilityId(): string {
    return this.facilityId$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public set filters(filters: Filter[]) {
    this.data.filter.updateFilters(filters)
  }

  public onCreate(_: Event): void {
    const facility = this.facilityId
    if (facility) {
      const days = this.data?.snapshot?.data ?? []
      const dayOffset = this.determineDayOffset(days)
      const period = Duration.ofMinutes(15)
      const newDay = <OptimisationDay>{
        facility,
        dayOffset,
        period,
      }
      this.create(newDay)
    }
  }

  public onRemoveAll(days: OptimisationDay[]): void {
    this.optimisationDayService.deleteAll(days).subscribe({
      next: (_) => {
        this.toastService.info('messages.deleting_successful')
      },
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private create(day: OptimisationDay): void {
    this.optimisationDayService.create(day).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private save(days: OptimisationDay[]): void {
    this.optimisationDayService.updateAll(days).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private determineDayOffset(days: OptimisationDay[]): number {
    if (days && days.length > 0) {
      return days[days.length - 1]?.dayOffset + 1
    } else {
      return 0
    }
  }
}
